import { Emitter } from './events.js';
import { ElenweaveGraph } from './graph.js';

export class ElenweaveWorkspace extends Emitter {
  constructor() {
    super();
    this.graphs = new Map();
    this.activeGraphId = null;
  }

  createGraph(name = 'Untitled') {
    const graph = new ElenweaveGraph({ name });
    this.graphs.set(graph.id, graph);
    if (!this.activeGraphId) this.setActiveGraph(graph.id);
    this.emit('graph:created', graph);
    return graph.id;
  }

  getGraph(id) {
    return this.graphs.get(id) || null;
  }

  listGraphs() {
    return Array.from(this.graphs.values());
  }

  renameGraph(id, name) {
    const graph = this.getGraph(id);
    if (!graph) return null;
    graph.name = name || graph.name;
    this.emit('graph:renamed', graph);
    return graph;
  }

  setActiveGraph(id) {
    if (id === this.activeGraphId) return;
    const graph = this.getGraph(id);
    if (!graph) return;
    this.activeGraphId = id;
    this.emit('graph:active', graph);
  }

  deleteGraph(id) {
    if (!this.graphs.has(id)) return false;
    this.graphs.delete(id);
    this.emit('graph:deleted', id);
    if (this.activeGraphId === id) {
      const next = this.listGraphs()[0];
      this.activeGraphId = null;
      if (next) this.setActiveGraph(next.id);
    }
    return true;
  }

  moveNode(nodeId, fromGraphId, toGraphId) {
    const from = this.getGraph(fromGraphId);
    const to = this.getGraph(toGraphId);
    if (!from || !to) return null;
    const node = from.getNode(nodeId);
    if (!node) return null;
    const payload = { ...node };
    payload.id = null;
    from.removeNode(nodeId);
    const newId = to.addNode(payload);
    this.emit('node:moved', { from: fromGraphId, to: toGraphId, fromId: nodeId, toId: newId });
    return newId;
  }

  addNodesToGraph(graphId, partials = []) {
    const graph = this.getGraph(graphId);
    if (!graph) return [];
    const ids = graph.addNodes(partials);
    if (ids.length) this.emit('nodes:added', { graphId, ids });
    return ids;
  }

  addEdgesToGraph(graphId, entries = []) {
    const graph = this.getGraph(graphId);
    if (!graph) return [];
    const ids = graph.addEdges(entries);
    if (ids.length) this.emit('edges:added', { graphId, ids });
    return ids;
  }

  updateNodesInGraph(graphId, entries = []) {
    const graph = this.getGraph(graphId);
    if (!graph) return [];
    const updated = graph.updateNodes(entries);
    if (updated.length) this.emit('nodes:updated', { graphId, ids: updated.map((n) => n.id) });
    return updated;
  }

  updateEdgesInGraph(graphId, entries = []) {
    const graph = this.getGraph(graphId);
    if (!graph) return [];
    const updated = graph.updateEdges(entries);
    if (updated.length) this.emit('edges:updated', { graphId, ids: updated.map((e) => e.id) });
    return updated;
  }

  addNodesBatch(entries = []) {
    if (!Array.isArray(entries)) return [];
    const results = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const graphId = entry.graphId ?? entry.graph ?? entry.boardId ?? null;
      if (!graphId) return;
      const graph = this.getGraph(graphId);
      if (!graph) return;
      const nodes = Array.isArray(entry.nodes)
        ? entry.nodes
        : Array.isArray(entry.partials)
          ? entry.partials
          : Array.isArray(entry.items)
            ? entry.items
            : [stripBatchEntry(entry)];
      const ids = graph.addNodes(nodes.filter(Boolean));
      if (ids.length) this.emit('nodes:added', { graphId, ids });
      ids.forEach((id) => results.push({ graphId, id }));
    });
    return results;
  }

  addEdgesBatch(entries = []) {
    if (!Array.isArray(entries)) return [];
    const results = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const graphId = entry.graphId ?? entry.graph ?? entry.boardId ?? null;
      if (!graphId) return;
      const graph = this.getGraph(graphId);
      if (!graph) return;
      const edges = Array.isArray(entry.edges)
        ? entry.edges
        : Array.isArray(entry.entries)
          ? entry.entries
          : Array.isArray(entry.items)
            ? entry.items
            : [stripBatchEntry(entry)];
      const ids = graph.addEdges(edges.filter(Boolean));
      if (ids.length) this.emit('edges:added', { graphId, ids });
      ids.forEach((id) => results.push({ graphId, id }));
    });
    return results;
  }

  updateNodesBatch(entries = []) {
    if (!Array.isArray(entries)) return [];
    const results = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const graphId = entry.graphId ?? entry.graph ?? entry.boardId ?? null;
      if (!graphId) return;
      const graph = this.getGraph(graphId);
      if (!graph) return;
      const updates = Array.isArray(entry.updates)
        ? entry.updates
        : Array.isArray(entry.entries)
          ? entry.entries
          : Array.isArray(entry.items)
            ? entry.items
            : [stripBatchEntry(entry)];
      const updated = graph.updateNodes(updates.filter(Boolean));
      if (updated.length) this.emit('nodes:updated', { graphId, ids: updated.map((n) => n.id) });
      updated.forEach((node) => results.push({ graphId, node }));
    });
    return results;
  }

  updateEdgesBatch(entries = []) {
    if (!Array.isArray(entries)) return [];
    const results = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const graphId = entry.graphId ?? entry.graph ?? entry.boardId ?? null;
      if (!graphId) return;
      const graph = this.getGraph(graphId);
      if (!graph) return;
      const updates = Array.isArray(entry.updates)
        ? entry.updates
        : Array.isArray(entry.entries)
          ? entry.entries
          : Array.isArray(entry.items)
            ? entry.items
            : [stripBatchEntry(entry)];
      const updated = graph.updateEdges(updates.filter(Boolean));
      if (updated.length) this.emit('edges:updated', { graphId, ids: updated.map((e) => e.id) });
      updated.forEach((edge) => results.push({ graphId, edge }));
    });
    return results;
  }

  exportGraph(id) {
    const graph = this.getGraph(id);
    if (!graph) return null;
    return graph.serialize();
  }

  exportWorkspace() {
    return {
      version: 'elenweave-workspace-v1',
      activeGraphId: this.activeGraphId,
      graphs: this.listGraphs().map((graph) => graph.serialize())
    };
  }

  importGraph(data, options = {}) {
    const payload = this._parseJson(data);
    if (!payload || !Array.isArray(payload.nodes) || !Array.isArray(payload.edges)) return null;
    const { id } = this._importGraphData(payload, options);
    if (options.makeActive || !this.activeGraphId) this.setActiveGraph(id);
    return id;
  }

  importWorkspace(data, options = {}) {
    const payload = this._parseJson(data);
    if (!payload || !Array.isArray(payload.graphs)) return false;
    const merge = Boolean(options.merge);
    if (!merge) this.clear();
    const idMap = new Map();
    payload.graphs.forEach((graphData) => {
      const { id, originalId } = this._importGraphData(graphData, { makeActive: false });
      idMap.set(originalId, id);
    });
    const desiredActive = payload.activeGraphId ? idMap.get(payload.activeGraphId) : null;
    if (desiredActive) this.setActiveGraph(desiredActive);
    if (!this.activeGraphId && this.graphs.size) {
      this.setActiveGraph(this.listGraphs()[0].id);
    }
    this.emit('workspace:imported', { graphCount: payload.graphs.length });
    return true;
  }

  clear() {
    this.graphs.clear();
    this.activeGraphId = null;
    this.emit('workspace:cleared');
  }

  _importGraphData(payload, options = {}) {
    const graph = ElenweaveGraph.hydrate(payload);
    const originalId = graph.id;
    const desiredId = options.id || graph.id;
    graph.id = this._uniqueGraphId(desiredId);
    if (options.name) graph.name = options.name;
    this.graphs.set(graph.id, graph);
    this.emit('graph:imported', graph);
    return { id: graph.id, originalId };
  }

  _parseJson(data) {
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (err) {
        console.warn('Invalid JSON payload', err);
        return null;
      }
    }
    return data;
  }

  _uniqueGraphId(preferred) {
    const base = preferred || this._generateGraphId();
    if (!this.graphs.has(base)) return base;
    let next;
    do {
      next = this._generateGraphId();
    } while (this.graphs.has(next));
    return next;
  }

  _generateGraphId() {
    return `g_${Math.random().toString(36).slice(2, 8)}`;
  }
}

function stripBatchEntry(entry) {
  const {
    graphId,
    graph,
    boardId,
    nodes,
    edges,
    partials,
    entries,
    updates,
    items,
    ...rest
  } = entry;
  return rest;
}

